import { useAuth } from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "desplega-config";

interface UserPreferences {
  sidebarCollapsed: boolean;

  // Legacy onboarding prefs — DO NOT use for new code. DB flags
  // (`useOrgFlags`) are the source of truth now (see plan 2026-04-24).
  // Kept as optional so the dead `BetterOnboarding` tree still typechecks.
  // No defaults / no per-org reset — reads will be `undefined`, which is
  // harmless for the never-rendered `BetterOnboarding` component.
  startedOnboarding?: boolean;
  skipOnboarding?: boolean;

  // --- Argus preferences ---
  // Added together in the Argus Frontend UI Phase 1 so the interface only grows once.
  // `argusSidebarOpen` is wired in Phase 3 (sliding panel); `argusAutoNavigate` in Phase 4b.
  argusSidebarOpen?: boolean;
  argusAutoNavigate?: boolean;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  sidebarCollapsed: false,

  argusSidebarOpen: false,
  // Default ON so the sidebar's navigate tool follows the agent's
  // recommendation out of the box. Users can opt out via preferences.
  argusAutoNavigate: true,
};

interface UserPreferencesContextValue {
  preferences: UserPreferences;
  updatePreferences: (updates: Partial<UserPreferences>) => void;
  resetPreferences: () => void;
}

const UserPreferencesContext = createContext<UserPreferencesContextValue | undefined>(undefined);

interface UserPreferencesProviderProps {
  children: ReactNode;
}

export const UserPreferencesProvider = ({ children }: UserPreferencesProviderProps) => {
  const { orgId } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES);
  const [isInitialized, setIsInitialized] = useState(false);

  // Get storage key for current org
  const getStorageKey = useCallback((currentOrgId: string | null | undefined) => {
    return currentOrgId ? `${STORAGE_KEY}-${currentOrgId}` : STORAGE_KEY;
  }, []);

  // Load preferences from localStorage when org changes
  useEffect(() => {
    if (!orgId && !isInitialized) {
      // Wait for orgId to be available on first mount
      return;
    }

    if (import.meta.env.PROD) {
      Sentry.setTag("org_id", orgId ?? "no-org");
    }

    try {
      const storageKey = getStorageKey(orgId);
      const stored = localStorage.getItem(storageKey);

      if (stored) {
        const parsed = JSON.parse(stored) as UserPreferences;
        setPreferences({ ...DEFAULT_PREFERENCES, ...parsed });
      } else {
        // No stored preferences for this org, use defaults
        setPreferences(DEFAULT_PREFERENCES);
      }
    } catch (error) {
      console.error("Failed to load user preferences:", error);
    } finally {
      setIsInitialized(true);
    }
  }, [orgId, getStorageKey, isInitialized]);

  // Save preferences to localStorage whenever they change (skip initial mount)
  useEffect(() => {
    if (!isInitialized || !orgId) return;

    try {
      const storageKey = getStorageKey(orgId);
      localStorage.setItem(storageKey, JSON.stringify(preferences));
    } catch (error) {
      console.error("Failed to save user preferences:", error);
    }
  }, [preferences, isInitialized, orgId, getStorageKey]);

  const updatePreferences = useCallback((updates: Partial<UserPreferences>) => {
    setPreferences((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetPreferences = useCallback(() => {
    setPreferences(DEFAULT_PREFERENCES);
  }, []);

  return (
    <UserPreferencesContext.Provider
      value={{
        preferences,
        updatePreferences,
        resetPreferences,
      }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error("useUserPreferences must be used within UserPreferencesProvider");
  }
  return context;
};
